/**
 * Contracts for post-accept assignment, geo matching snapshots, and offer enrichment.
 * Keep in sync with matching + business profile services.
 */
import { GHALISHOR_API_V1_PREFIX } from './identity-access';
import type { IsoDateTimeString, UuidString } from './identity-access';
import type {
  CarpetType,
  MatchingFlowType,
  ServiceCatalogKey,
  ServiceRequestStatus,
  ServiceRequestSummary,
} from './customer-requests';

export const OFFER_BADGES = Object.freeze([
  'fast_response',
  'high_guarantee',
  'economical',
  'nearby',
  'top_rated',
  'fast_turnaround',
] as const);
export type OfferBadge = (typeof OFFER_BADGES)[number];

export const SCHEDULE_STATES = Object.freeze([
  'none',
  'proposed',
  'counter_proposed',
  'confirmed',
] as const);
export type ScheduleState = (typeof SCHEDULE_STATES)[number];

export const SCHEDULE_ROLES = Object.freeze(['business', 'customer'] as const);
export type ScheduleRole = (typeof SCHEDULE_ROLES)[number];

/** Hours before a business-proposed schedule auto-confirms if the customer is silent. */
export const SCHEDULE_CONFIRM_WINDOW_HOURS = 1;

export const ASSIGNMENT_ROUTES = Object.freeze({
  customerAssignment: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/customer/requests/${encodeURIComponent(id)}/assignment`,
  businessAssignment: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/business/requests/${encodeURIComponent(id)}/assignment`,
  customerSchedule: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/customer/requests/${encodeURIComponent(id)}/schedule`,
  customerScheduleConfirm: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/customer/requests/${encodeURIComponent(id)}/schedule/confirm`,
  customerScheduleCounter: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/customer/requests/${encodeURIComponent(id)}/schedule/counter`,
  businessSchedule: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/business/requests/${encodeURIComponent(id)}/schedule`,
  businessScheduleConfirm: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/business/requests/${encodeURIComponent(id)}/schedule/confirm`,
  businessActive: `${GHALISHOR_API_V1_PREFIX}/business/requests/active`,
  customerReview: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/customer/requests/${encodeURIComponent(id)}/review`,
  businessProposePrice: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/business/requests/${encodeURIComponent(id)}/price`,
  customerConfirmPrice: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/customer/requests/${encodeURIComponent(id)}/price/confirm`,
  customerRejectPrice: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/customer/requests/${encodeURIComponent(id)}/price/reject`,
  businessProfile: `${GHALISHOR_API_V1_PREFIX}/business/profile`,
  businessRateCards: `${GHALISHOR_API_V1_PREFIX}/business/rate-cards`,
} as const);

export interface ScheduleRequestBody {
  readonly pickupAt: IsoDateTimeString;
  readonly returnAt: IsoDateTimeString;
  readonly notes?: string | null;
}

export interface ProposeConfirmedPriceRequest {
  readonly confirmedPrice: number;
  readonly note?: string | null;
}

export interface ConfirmPriceResponse {
  readonly request: ServiceRequestSummary;
}

export interface CreateReviewRequest {
  readonly rating: number;
  readonly comment?: string | null;
}

export interface AssignmentPeer {
  readonly displayName: string | null;
  readonly mobileE164: string;
  readonly city: string | null;
  readonly addressText: string | null;
  /** Exact destination is exposed only after the request is accepted. */
  readonly latitude: number | null;
  readonly longitude: number | null;
}

export interface AssignmentSummary {
  readonly request: ServiceRequestSummary;
  readonly peer: AssignmentPeer;
  readonly businessName: string;
  readonly businessId: UuidString;
  readonly distanceKm: number | null;
  readonly estimatedPrice: number | null;
  readonly confirmedPrice: number | null;
  readonly scheduledPickupAt: IsoDateTimeString | null;
  readonly scheduledReturnAt: IsoDateTimeString | null;
  readonly scheduleNotes: string | null;
  readonly scheduleState: ScheduleState;
  readonly scheduleProposedByRole: ScheduleRole | null;
  readonly scheduleConfirmDeadlineAt: IsoDateTimeString | null;
  readonly scheduleConfirmedAt: IsoDateTimeString | null;
  readonly canCall: boolean;
  readonly canProposeSchedule: boolean;
  readonly canConfirmSchedule: boolean;
  readonly canCounterSchedule: boolean;
  /** @deprecated use canProposeSchedule / canConfirmSchedule / canCounterSchedule */
  readonly canSchedule: boolean;
  readonly canReview: boolean;
  readonly canProposePrice: boolean;
  readonly canConfirmPrice: boolean;
}

export interface BusinessProfileResponse {
  readonly businessId: UuidString;
  readonly city: string | null;
  readonly addressText: string | null;
  readonly lat: number | null;
  readonly lng: number | null;
  readonly serviceRadiusKm: number;
  readonly typicalReturnDays: number | null;
  readonly autoQuoteEnabled: boolean;
  readonly autoScheduleEnabled: boolean;
  readonly pickupLeadHours: number;
  readonly preferredPickupHour: number;
  readonly updatedAt: IsoDateTimeString;
}

export interface UpdateBusinessProfileRequest {
  readonly city?: string | null;
  readonly addressText?: string | null;
  readonly lat?: number | null;
  readonly lng?: number | null;
  readonly serviceRadiusKm?: number;
  readonly typicalReturnDays?: number | null;
  readonly autoQuoteEnabled?: boolean;
  readonly autoScheduleEnabled?: boolean;
  readonly pickupLeadHours?: number;
  readonly preferredPickupHour?: number;
}

export interface BusinessRateCard {
  readonly carpetType: CarpetType;
  readonly serviceKey: ServiceCatalogKey;
  readonly pricePerSqm: number;
  readonly minPrice: number;
  readonly currency: string;
}

export interface UpsertBusinessRateCardsRequest {
  readonly items: readonly BusinessRateCard[];
}

export interface EnrichedOfferFields {
  readonly distanceKm: number | null;
  readonly estimatedPrice: number | null;
  readonly ratingAvg: number | null;
  readonly ratingCount: number;
  readonly matchScore: number | null;
  readonly badges: readonly OfferBadge[];
}

export interface ActiveBusinessJob {
  readonly serviceRequestId: UuidString;
  readonly status: ServiceRequestStatus;
  readonly carpetType: CarpetType;
  readonly carpetCount: number;
  readonly approximateSqm: number;
  readonly services: readonly ServiceCatalogKey[];
  readonly flowType: MatchingFlowType | null;
  readonly customerCity: string | null;
  readonly scheduledPickupAt: IsoDateTimeString | null;
  readonly scheduledReturnAt: IsoDateTimeString | null;
  readonly scheduleState: ScheduleState | null;
  readonly updatedAt: IsoDateTimeString;
}

export function isOfferBadge(value: unknown): value is OfferBadge {
  return typeof value === 'string' && (OFFER_BADGES as readonly string[]).includes(value);
}
